import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { FundedDeal } from 'src/@types/funded-deals';
import * as fundedDealsAPI from 'src/services/APIs/fundedDealsAPI';
import * as dealsApi from 'src/services/APIs/dealsApi';

// ----------------------------------------------------------------------

export type FundedDealsState = {
  isLoading: boolean;
  error: Error | string | null;
  fundedDeals: FundedDeal[];
};

export const initialState: FundedDealsState = {
  isLoading: false,
  error: null,
  fundedDeals: [] as FundedDeal[],
};

const slice = createSlice({
  name: 'fundedDeals',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET Data
    getFundedDealsSuccess(state, action) {
      state.isLoading = false;
      state.fundedDeals = action.payload;
    },

    updateFundedDealsData(state, action) {
      state.fundedDeals = action.payload;
    },

    resetFundedDealsData(state) {
      state.fundedDeals = [];
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { resetFundedDealsData, updateFundedDealsData } = slice.actions;

// ----------------------------------------------------------------------
// Define functions that fetch/mutate data and update the Redux state

export const getFundedDeals = createAsyncThunk('getFundedDeals', async (_, { dispatch }) => {
  dispatch(slice.actions.startLoading());
  try {
    const fundedDeals: FundedDeal[] = await fundedDealsAPI.fetchDeals();
    if (fundedDeals) {
      dispatch(slice.actions.getFundedDealsSuccess(fundedDeals));
    }
  } catch (error) {
    dispatch(slice.actions.hasError(error));
  }
});

export const requestPayoffLetter = createAsyncThunk(
  'requestPayoffLetter',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const {
      // @ts-ignore
      mainApp: { userData },
      // @ts-ignore
      fundedDeals: { fundedDeals },
    } = getState();

    dispatch(slice.actions.startLoading());
    try {
      const clientId = localStorage.getItem('clientId') ?? '';
      const username = userData?.firstName + ' ' + userData?.lastName;
      const fundingId = fundedDeals.length > 0 ? fundedDeals[fundedDeals.length - 1].id : '';
      const dealId = fundedDeals.length > 0 ? fundedDeals[fundedDeals.length - 1].dealId : '';
      const response = await fundedDealsAPI.requestPayoffLetter(
        clientId,
        username,
        fundingId,
        dealId
      );
      if (response.success) {
        return { status: 'success' };
      } else {
        return rejectWithValue({ status: 'failed' });
      }
    } catch (error) {
      return rejectWithValue({ status: 'failed' });
    }
  }
);

export const requestZeroBalanceLetter = createAsyncThunk(
  'requestZeroBalanceLetter',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const {
      // @ts-ignore
      mainApp: { userData },
      // @ts-ignore
      fundedDeals: { fundedDeals },
    } = getState();
    dispatch(slice.actions.startLoading());
    try {
      const clientId = localStorage.getItem('clientId') ?? '';
      const username = userData?.firstName + ' ' + userData?.lastName;
      const fundingId =
        fundedDeals.length > 0 ? fundedDeals[fundedDeals.length - 1].identifier : '';
      const dealId = fundedDeals.length > 0 ? fundedDeals[fundedDeals.length - 1].dealId : '';
      const res = await fundedDealsAPI.requestZeroBalanceLetter(
        clientId,
        username,
        fundingId,
        dealId
      );
      if (res.success) {
        return { status: 'success' };
      } else {
        return rejectWithValue({ status: 'failed' });
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return rejectWithValue({ status: 'failed' });
    }
  }
);

export const requestRenewal = createAsyncThunk(
  'requestRenewal',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const {
      // @ts-ignore
      mainApp: { userData },
      // @ts-ignore
      fundedDeals: { fundedDeals },
    } = getState();

    dispatch(slice.actions.startLoading());

    try {
      const clientId = localStorage.getItem('clientId') ?? '';
      const username = userData?.firstName + ' ' + userData?.lastName;
      const fundingId = fundedDeals.length > 0 ? fundedDeals[fundedDeals.length - 1].id : '';
      const dealId = fundedDeals.length > 0 ? fundedDeals[fundedDeals.length - 1].dealId : '';
      const res = await fundedDealsAPI.requestRenewal(clientId, username, fundingId, dealId);
      if (res.success) {
        return { status: 'success' };
      } else {
        return rejectWithValue({ status: 'failed' });
      }
    } catch (error) {
      return rejectWithValue({ status: 'failed' });
    }
  }
);

export const requestToChangeBankAccount = createAsyncThunk<
  { status: string },
  string,
  { rejectValue: { status: string } }
>('requestToChangeBankAccount', async (businessName, { dispatch, getState, rejectWithValue }) => {
  const {
    // @ts-ignore
    mainApp: { userData },
    // @ts-ignore
    fundedDeals: { fundedDeals },
  } = getState();

  dispatch(slice.actions.startLoading());

  try {
    const username = userData?.firstName + ' ' + userData?.lastName;
    const fundingId = fundedDeals.length > 0 ? fundedDeals[fundedDeals.length - 1].id : '';
    const dealId = fundedDeals.length > 0 ? fundedDeals[fundedDeals.length - 1].dealId : '';

    const res = await dealsApi.postCustomerSupportSendEmail({
      notes: '',
      fromEmail: userData?.emailAddress,
      businessName,
      username,
      dealId,
      fundingId,
    });

    if (res.success) {
      return { status: 'success' };
    } else {
      return rejectWithValue({ status: 'failed' });
    }
  } catch (error) {
    return rejectWithValue({ status: 'failed' });
  }
});

// Load from MCA Funds
export const getFundedDealByAppId = createAsyncThunk(
  'getFundedDealByAppId/getFundedDealByAppId',
  // eslint-disable-next-line no-unused-vars
  async (payload: any, { dispatch, getState }) => {
    dispatch(slice.actions.startLoading());
    try {
      await fundedDealsAPI.getFundedDealFromMCAFunds(payload);
      const fundedDeals: FundedDeal[] = await fundedDealsAPI.fetchDeals();
      if (fundedDeals) {
        dispatch(slice.actions.getFundedDealsSuccess(fundedDeals));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  }
);

// Get from MCA Funds
export const getFundedDealsByCompanyId = createAsyncThunk(
  'getFundedDealsByCompanyId/getFundedDealsByCompanyId',
  // eslint-disable-next-line no-unused-vars
  async (_, { dispatch, getState }) => {
    dispatch(slice.actions.startLoading());
    try {
      const {
        // @ts-ignore
        admin: { client },
      } = getState();
      if (!client) return;

      const fundedDeals: FundedDeal[] = await fundedDealsAPI.getFundedDealsByCompanyId(
        client.companyId
      );
      if (fundedDeals) {
        dispatch(slice.actions.getFundedDealsSuccess(fundedDeals));
      }
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  }
);
